import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Search, 
  Download, Printer, Clock, AlertCircle, 
  ChevronRight, ChevronDown,
  Fingerprint, Briefcase,
  Loader2, CircleHelp
} from 'lucide-react';
import OnboardingTour from '../../../components/ERP/Onboarding/OnboardingTour';
import { useAuth } from '../../../contexts/AuthContext';
import { ATTENDANCE_LOG_ONBOARDING_STEPS, ATTENDANCE_LOG_TOUR_ID } from './attendanceLogOnboarding';
import Avatar from '../../../components/Avatar/Avatar';
import AttendanceLogPdfCanvas, { AttendanceLogPdfPayload } from '../../../components/ERP/Documents/AttendanceLogPdfCanvas';
import { exportElementToPdf } from '../../../utils/exportElementToPdf';
import { attendanceService } from '../../../services/api';
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions';
import './AttendanceLogPage.css';

type EntrySlot = 'in1' | 'out1' | 'in2' | 'out2';

function hasAttendanceSignature(value?: string | null) {
  return Boolean(value && !String(value).startsWith('plantao'));
}

const ENTRY_SLOT_LABELS: Record<EntrySlot, string> = {
  in1: 'Entrada 1',
  out1: 'Saída 1',
  in2: 'Entrada 2',
  out2: 'Saída 2',
};

// Tipos
interface Employee {
  id: number;
  name: string;
  email: string;
  role?: string;
  department?: string;
  avatar?: string;
  access_group_id?: number;
}

interface MedicalCertificate {
  id: number;
  user_id: number;
  date: string;
  start_time: string;
  end_time: string;
  description?: string;
}

interface DailyLog {
  isoDate: string; // YYYY-MM-DD
  date: string;
  weekday: string;
  entries: { in1: string; out1: string; in2: string; out2: string };
  /** IDs dos registros de ponto no banco (para edição) */
  entryLogIds?: Partial<Record<EntrySlot, number>>;
  total: string;
  balance: string; // "+01:00" or "-00:30"
  status: 'normal' | 'extra' | 'delay' | 'absent' | 'weekend';
  signed: boolean;
  certificates?: MedicalCertificate[];
  certificateHours?: number; // Horas dos atestados em minutos
  hasFullCertificate?: boolean; // Indica se tem atestado completo
  hasPartialCertificate?: boolean; // Indica se tem atestado parcial
}

interface AttendanceLog {
  id: number;
  user_id: number;
  type: 'in' | 'out';
  timestamp: string;
  date: string;
  time: string;
  location?: string;
  signature: string;
}

const SLOT_TO_TYPE: Record<EntrySlot, 'in' | 'out'> = {
  in1: 'in',
  out1: 'out',
  in2: 'in',
  out2: 'out',
};

const PLANTAO_MINUTES = 12 * 60;

function addDaysIso(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function logDateKey(log: { date?: string }): string {
  let dateKey = log.date || '';
  if (!dateKey) return '';
  if (dateKey.includes('T')) dateKey = dateKey.split('T')[0];
  if (!dateKey.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const dateObj = new Date(dateKey);
    if (Number.isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    dateKey = `${year}-${month}-${day}`;
  }
  return dateKey;
}

const AttendanceLogPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canEditTimes = hasPermission(MODULES.ATTENDANCE, ACTIONS.ATTENDANCE_LOG_EDIT);
  const [tourReplay, setTourReplay] = useState(0);
  const [searchParams] = useSearchParams();
  const userIdParam = searchParams.get('userId');
  const monthParam = searchParams.get('month');
  const yearParam = searchParams.get('year');
  
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(userIdParam || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<number>(monthParam ? parseInt(monthParam) : new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(yearParam ? parseInt(yearParam) : new Date().getFullYear());
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [monthlyLogs, setMonthlyLogs] = useState<DailyLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [editModal, setEditModal] = useState<{
    logId: number | null;
    isoDate: string;
    slot: EntrySlot;
    slotLabel: string;
    draftTime: string;
    dayDisplay: string;
  } | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [exportPdfPayload, setExportPdfPayload] = useState<AttendanceLogPdfPayload | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  // Carregar lista de colaboradores
  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar logs quando mudar usuário ou período
  useEffect(() => {
    if (selectedEmployeeId) {
      loadLogs(parseInt(selectedEmployeeId), selectedYear, selectedMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeId, selectedMonth, selectedYear]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowEmployeeDropdown(false);
      }
    };

    if (showEmployeeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmployeeDropdown]);

  useEffect(() => {
    if (!editModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditModal(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editModal]);

  const openTimeEdit = (
    log: DailyLog,
    slot: EntrySlot,
    logId: number | null,
    displayTime: string
  ) => {
    const hhmm =
      displayTime && displayTime !== '-'
        ? displayTime.slice(0, 5)
        : '08:00';
    setEditModal({
      logId,
      isoDate: log.isoDate,
      slot,
      slotLabel: ENTRY_SLOT_LABELS[slot],
      draftTime: hhmm,
      dayDisplay: `${log.date} (${log.weekday})`,
    });
  };

  const saveTimeEdit = async () => {
    if (!editModal || !selectedEmployeeId) return;
    setSavingEdit(true);
    try {
      const isUpdate = editModal.logId != null;
      const res = isUpdate
        ? await attendanceService.patchLog(editModal.logId, {
            time: editModal.draftTime,
          })
        : await attendanceService.createManualLog({
            userId: parseInt(selectedEmployeeId, 10),
            date: editModal.isoDate,
            type: SLOT_TO_TYPE[editModal.slot],
            time: editModal.draftTime,
            notes: `Ajuste manual no espelho de ponto (${editModal.slotLabel})`,
          });
      if (res.success) {
        setEditModal(null);
        await loadLogs(parseInt(selectedEmployeeId, 10), selectedYear, selectedMonth);
      } else {
        alert((res as { message?: string }).message || 'Não foi possível salvar o horário.');
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      alert(ax.response?.data?.message || ax.message || 'Erro ao salvar o horário.');
    } finally {
      setSavingEdit(false);
    }
  };

  const renderTimeCell = (log: DailyLog, slot: EntrySlot) => {
    const value = log.entries[slot];
    const logId = log.entryLogIds?.[slot] ?? null;
    const editable = canEditTimes && log.status !== 'weekend';

    if (editable) {
      return (
        <td
          key={slot}
          className="text-center attendance-log-cell-time attendance-log-cell-time--editable"
          onClick={() => openTimeEdit(log, slot, logId, value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openTimeEdit(log, slot, logId, value);
            }
          }}
          role="button"
          tabIndex={0}
          title={logId ? 'Clique para editar o horário' : 'Clique para adicionar o horário'}
        >
          {value}
        </td>
      );
    }
    return (
      <td key={slot} className="text-center attendance-log-cell-time">
        {value}
      </td>
    );
  };

  const handleEmployeeSelect = (employeeId: string) => {
    setSelectedEmployeeId(employeeId);
    setShowEmployeeDropdown(false);
    setSearchTerm('');
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await attendanceService.getEmployees();
      if (response.success && response.data) {
        const employeesList = response.data.map((employee: any) => ({
          id: employee.id,
          name: employee.name || 'Usuário',
          email: employee.email || '',
          avatar: employee.avatar_url || undefined,
          role: 'Colaborador',
          department: 'Geral'
        }));
        setEmployees(employeesList);
        
        // Se não há userId selecionado e há colaboradores, selecionar o primeiro
        if (!selectedEmployeeId && employeesList.length > 0) {
          setSelectedEmployeeId(employeesList[0].id.toString());
        }
      }
    } catch (err: any) {
      console.error('Erro ao carregar colaboradores:', err);
      setError(err.response?.data?.message || 'Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (userId: number, year: number, month: number) => {
    try {
      setLoadingLogs(true);
      setError(null);

      // Calcular período
      const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthEnd = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      const dateFrom = addDaysIso(monthStart, -1);
      const dateTo = addDaysIso(monthEnd, 1);
      
      // Buscar logs e atestados em paralelo
      const [logsResponse, certsResponse] = await Promise.all([
        attendanceService.getLogs({
          userId,
          dateFrom,
          dateTo
        }),
        attendanceService.getMedicalCertificates({
          userId,
          dateFrom,
          dateTo
        })
      ]);

      if (logsResponse.success && logsResponse.data) {
        const logs: AttendanceLog[] = logsResponse.data;
        const certificates = certsResponse.success ? certsResponse.data : [];
        
        const dailyLogs = processLogsToDaily(logs, year, month, certificates);
        setMonthlyLogs(dailyLogs);
      } else {
        setMonthlyLogs([]);
      }
    } catch (err: any) {
      console.error('Erro ao carregar logs:', err);
      setError(err.response?.data?.message || 'Erro ao carregar registros de ponto');
      setMonthlyLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const processLogsToDaily = (logs: AttendanceLog[], year: number, month: number, certificates: MedicalCertificate[] = []): DailyLog[] => {
    const dailyMap = new Map<string, AttendanceLog[]>();
    
    // Agrupar logs por data
    logs.forEach(log => {
      const dateKey = logDateKey(log);
      if (!dateKey) return;
      if (!dailyMap.has(dateKey)) {
        dailyMap.set(dateKey, []);
      }
      dailyMap.get(dateKey)!.push(log);
    });

    const ordered = [...logs]
      .map((log) => ({ log, dateKey: logDateKey(log) }))
      .filter((item) => item.dateKey)
      .sort((a, b) => new Date(a.log.timestamp).getTime() - new Date(b.log.timestamp).getTime());
    const plantaoByStartDate = new Map<string, { inLog: AttendanceLog; outLog: AttendanceLog }>();
    const plantaoInIds = new Set<number>();
    const plantaoOutIds = new Set<number>();
    let pairIndex = 0;
    while (pairIndex < ordered.length) {
      const current = ordered[pairIndex];
      const next = ordered[pairIndex + 1];
      if (
        current?.log.type === 'in'
        && next?.log.type === 'out'
        && addDaysIso(current.dateKey, 1) === next.dateKey
      ) {
        plantaoByStartDate.set(current.dateKey, { inLog: current.log, outLog: next.log });
        plantaoInIds.add(current.log.id);
        plantaoOutIds.add(next.log.id);
        pairIndex += 2;
        continue;
      }
      pairIndex += 1;
    }

    const lastDay = new Date(year, month, 0).getDate();
    const dailyLogs: DailyLog[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas de data

    for (let day = 1; day <= lastDay; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const date = new Date(year, month - 1, day);
      date.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas de data
      const weekday = date.getDay();
      
      // Pular dias futuros (após a data atual)
      if (date > today) {
        continue;
      }
      
      const dayLogs = dailyMap.get(dateStr) || [];
      
      // Ordenar logs por timestamp (mais confiável que time)
      const sortedLogs = dayLogs.sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        }
        // Fallback para ordenação por time
        return (a.time || '').localeCompare(b.time || '');
      });
      const plantao = plantaoByStartDate.get(dateStr);
      const closingOuts = sortedLogs.filter((log) => plantaoOutIds.has(log.id));
      const sameDayLogs = sortedLogs.filter((log) => !plantaoInIds.has(log.id) && !plantaoOutIds.has(log.id));

      // Domingo é folga, exceto quando começa um plantão
      if (weekday === 0 && !plantao) {
        dailyLogs.push({
          isoDate: dateStr,
          date: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
          weekday: 'Domingo',
          entries: {
            in1: '-',
            out1: closingOuts[0] ? closingOuts[0].time.substring(0, 5) : '-',
            in2: '-',
            out2: '-'
          },
          entryLogIds: closingOuts[0]?.id ? { out1: closingOuts[0].id } : undefined,
          total: '00:00',
          balance: '00:00',
          status: 'weekend',
          signed: sortedLogs.some((log) => hasAttendanceSignature(log.signature))
        });
        continue;
      }

      // Sábado: dia útil com horário especial (09:00 às 10:39)
      const isSaturday = weekday === 6;

      // Processar entradas e saídas
      const entries = { in1: '-', out1: '-', in2: '-', out2: '-' };
      let totalMinutes = 0;
      let status: DailyLog['status'] = 'normal';
      let balance = '00:00';

      // Identificar entradas e saídas do mesmo dia. Plantão usa a saída do dia seguinte.
      const ins = sameDayLogs.filter(l => l.type === 'in');
      const outs = sameDayLogs.filter(l => l.type === 'out');
      let isPlantao = false;
      let entryLogIds: Partial<Record<EntrySlot, number>> = {};

      if (ins.length > 0) entries.in1 = ins[0].time.substring(0, 5);
      if (outs.length > 0) entries.out1 = outs[0].time.substring(0, 5);
      if (ins.length > 1) entries.in2 = ins[1].time.substring(0, 5);
      if (outs.length > 1) entries.out2 = outs[1].time.substring(0, 5);

      // Calcular total de horas trabalhadas
      // Segunda a Sexta: 08:30 às 17:58 com 1 hora de intervalo (jornada: 8h28min = 508 minutos)
      // Sábado: 09:00 às 10:39 (jornada: 1h39min = 99 minutos)
      
      let expectedEntry: number;
      let expectedExit: number;
      let expectedMinutes: number;
      
      if (isSaturday) {
        // Sábado: 09:00 às 10:39
        expectedEntry = 9 * 60 + 0; // 09:00
        expectedExit = 10 * 60 + 39; // 10:39
        expectedMinutes = 99; // 1h39min
      } else {
        // Segunda a Sexta: 08:30 às 17:58
        expectedEntry = 8 * 60 + 30; // 08:30
        expectedExit = 17 * 60 + 58; // 17:58
        expectedMinutes = 508; // 8h28min
      }
      
      if (plantao) {
        isPlantao = true;
        entries.in1 = plantao.inLog.time.substring(0, 5);
        entries.out1 = plantao.outLog.time.substring(0, 5);
        entryLogIds = { in1: plantao.inLog.id, out1: plantao.outLog.id };
        totalMinutes = PLANTAO_MINUTES;
        expectedMinutes = PLANTAO_MINUTES;
        status = 'normal';
        balance = '00:00';
      } else if (ins.length > 0 && outs.length > 0) {
        // Primeiro período: primeira entrada até primeira saída
        const time1 = calculateTimeDifference(ins[0].time, outs[0].time);
        
        // Segundo período: segunda entrada até segunda saída (se houver)
        // Nota: Sábados normalmente têm apenas um período
        const time2 = ins.length > 1 && outs.length > 1 
          ? calculateTimeDifference(ins[1].time, outs[1].time)
          : 0;
        
        totalMinutes = time1 + time2;
        
        // Verificar atraso na entrada
        const [entryHours, entryMinutes] = ins[0].time.split(':').map(Number);
        const entryTime = entryHours * 60 + entryMinutes;
        
        // Verificar hora extra na saída
        const [exitHours, exitMinutes] = outs[outs.length - 1].time.split(':').map(Number);
        const exitTime = exitHours * 60 + exitMinutes;
        
        // Calcular diferença em relação à jornada esperada
        const difference = totalMinutes - expectedMinutes;
        
        // Prioridade: atraso na entrada > hora extra na saída > diferença de jornada
        if (entryTime > expectedEntry) {
          // Atraso na entrada
          status = 'delay';
          const delayMinutes = entryTime - expectedEntry;
          balance = `-${formatTime(delayMinutes)}`;
        } else if (exitTime > expectedExit) {
          // Hora extra na saída
          status = 'extra';
          const extraMinutes = exitTime - expectedExit;
          balance = `+${formatTime(extraMinutes)}`;
        } else if (difference > 0) {
          // Trabalhou mais que o esperado (hora extra)
          status = 'extra';
          balance = `+${formatTime(difference)}`;
        } else if (difference < -60) {
          // Trabalhou muito menos que o esperado (mais de 1 hora) - pode ser falta parcial
          status = 'absent';
          balance = formatTime(Math.abs(difference));
        } else {
          // Normal ou pequena diferença
          status = 'normal';
          balance = difference !== 0 ? (difference > 0 ? '+' : '-') + formatTime(Math.abs(difference)) : '00:00';
        }
      } else if (!isPlantao && ins.length > 0) {
        // Tem entrada mas não tem saída - verificar se atrasou
        const [entryHours, entryMinutes] = ins[0].time.split(':').map(Number);
        const entryTime = entryHours * 60 + entryMinutes;
        
        if (entryTime > expectedEntry) {
          status = 'delay';
          const delayMinutes = entryTime - expectedEntry;
          balance = `-${formatTime(delayMinutes)}`;
        }
      }

      // Verificar falta (nenhum registro do expediente). A saída do plantão não é falta.
      if (!isPlantao && sameDayLogs.length === 0) {
        if (closingOuts.length > 0) {
          entries.out1 = closingOuts[0].time.substring(0, 5);
          entryLogIds.out1 = closingOuts[0].id;
          status = 'normal';
          totalMinutes = 0;
          balance = '00:00';
        } else {
          status = 'absent';
          totalMinutes = 0;
          balance = '00:00';
        }
      }

      // Buscar atestados médicos para este dia
      const dayCertificates = certificates.filter((cert: MedicalCertificate) => {
        let certDate = cert.date || '';
        if (certDate.includes('T')) {
          certDate = certDate.split('T')[0];
        }
        return certDate === dateStr;
      });

      // Calcular horas dos atestados e verificar se é completo ou parcial
      let certificateMinutes = 0;
      let hasFullCertificate = false;
      let hasPartialCertificate = false;
      
      dayCertificates.forEach((cert: MedicalCertificate) => {
        const [startH, startM] = cert.start_time.split(':').map(Number);
        const [endH, endM] = cert.end_time.split(':').map(Number);
        const startTotal = startH * 60 + startM;
        const endTotal = endH * 60 + endM;
        
        // Verificar se cobre o horário completo de trabalho
        let isFullDay = false;
        if (isSaturday) {
          // Sábado: 09:00 às 10:39 (sem intervalo)
          if (startTotal <= 9 * 60 && endTotal >= 10 * 60 + 39) {
            isFullDay = true;
            hasFullCertificate = true;
          } else {
            hasPartialCertificate = true;
          }
        } else {
          // Segunda a Sexta: 08:30 às 17:58 com 1 hora de intervalo
          if (startTotal <= 8 * 60 + 30 && endTotal >= 17 * 60 + 58) {
            isFullDay = true;
            hasFullCertificate = true;
          } else {
            hasPartialCertificate = true;
          }
        }
        
        // Calcular horas brutas do atestado
        let certMinutes = endTotal - startTotal;
        
        // Se o atestado cobrir o horário completo de trabalho, descontar o intervalo
        if (isFullDay && !isSaturday) {
          // Atestado cobre o horário completo, descontar 1 hora (60 minutos) do intervalo
          certMinutes -= 60;
        }
        
        certificateMinutes += certMinutes;
      });

      // Adicionar horas dos atestados ao total
      totalMinutes += certificateMinutes;

      // Recalcular balance se houver atestados
      if (!isPlantao && certificateMinutes > 0 && dayLogs.length > 0) {
        const newDifference = totalMinutes - expectedMinutes;
        if (newDifference > 0) {
          status = 'extra';
          balance = `+${formatTime(newDifference)}`;
        } else if (newDifference < -60) {
          status = 'absent';
          balance = formatTime(Math.abs(newDifference));
        } else {
          status = 'normal';
          balance = newDifference !== 0 ? (newDifference > 0 ? '+' : '-') + formatTime(Math.abs(newDifference)) : '00:00';
        }
      }

      const total = formatTime(totalMinutes);
      const signed = dayLogs.some((log) => hasAttendanceSignature(log.signature));

      if (!isPlantao && !entryLogIds.out1) {
        if (ins[0]?.id) entryLogIds.in1 = ins[0].id;
        if (outs[0]?.id) entryLogIds.out1 = outs[0].id;
        if (ins[1]?.id) entryLogIds.in2 = ins[1].id;
        if (outs[1]?.id) entryLogIds.out2 = outs[1].id;
      }

      dailyLogs.push({
        isoDate: dateStr,
        date: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
        weekday: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][weekday],
        entries,
        entryLogIds,
        total,
        balance,
        status,
        signed,
        certificates: dayCertificates.length > 0 ? dayCertificates : undefined,
        certificateHours: certificateMinutes > 0 ? certificateMinutes : undefined,
        hasFullCertificate: hasFullCertificate,
        hasPartialCertificate: hasPartialCertificate
      });
    }

    return dailyLogs;
  };

  const calculateTimeDifference = (time1: string, time2: string): number => {
    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);
    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;
    return Math.max(0, minutes2 - minutes1);
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  const filteredEmployees = employees.filter(e => 
    e.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (e.email && e.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const activeEmployee = employees.find(e => e.id.toString() === selectedEmployeeId) || employees[0];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'normal': return <span className="attendance-log-status-normal">Normal</span>;
      case 'extra': return <span className="attendance-log-status-extra">Hora Extra</span>;
      case 'delay': return <span className="attendance-log-status-delay">Atraso</span>;
      case 'weekend': return <span className="attendance-log-status-weekend">DSR / Folga</span>;
      case 'absent': return <span className="attendance-log-status-absent">Falta</span>;
      default: return null;
    }
  };

  const getCertificateBadge = (hasFull: boolean, hasPartial: boolean) => {
    if (hasFull) {
      return <span className="attendance-log-certificate-total">total</span>;
    } else if (hasPartial) {
      return <span className="attendance-log-certificate-partial">parcial</span>;
    }
    return null;
  };

  const getMonthName = (month: number): string => {
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return months[month - 1] || '';
  };

  const handlePreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const mountExportPayload = () => {
    if (!activeEmployee || monthlyLogs.length === 0) return null;
    return {
      employee: {
        id: activeEmployee.id,
        name: activeEmployee.name,
        department: activeEmployee.department,
      },
      logs: monthlyLogs,
      month: selectedMonth,
      year: selectedYear,
    };
  };

  const collectMirrorCss = () => {
    let css = '';
    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList;
      try {
        rules = sheet.cssRules;
      } catch {
        continue;
      }
      for (const rule of Array.from(rules)) {
        if (/al-pdf|ar-pdf/.test(rule.cssText)) css += `${rule.cssText}\n`;
      }
    }
    return css;
  };

  const handlePrint = async () => {
    if (!activeEmployee || monthlyLogs.length === 0) {
      alert('Não há registros para imprimir no período selecionado.');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      alert('Permita janelas pop-up para imprimir o espelho.');
      return;
    }

    setPrinting(true);
    try {
      setExportPdfPayload(mountExportPayload());
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (!pdfRef.current) {
        throw new Error('Falha ao montar o espelho');
      }

      const title = `Espelho de Ponto — ${activeEmployee.name}`;
      printWindow.document.open();
      printWindow.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    ${collectMirrorCss()}
    @page { size: landscape; margin: 10mm; }
    body { margin: 0; background: #fff; }
    .al-pdf-canvas { width: 100%; border: none; }
    .no-print { display: flex; gap: 8px; padding: 12px 16px; }
    @media print { .no-print { display: none !important; } }
  </style>
</head>
<body>
  <div class="no-print">
    <button type="button" onclick="window.print()">Imprimir</button>
    <button type="button" onclick="window.close()">Fechar</button>
  </div>
  ${pdfRef.current.outerHTML}
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print() }, 150)
    })
  </script>
</body>
</html>`);
      printWindow.document.close();
      printWindow.focus();
    } catch (error) {
      console.error('Erro ao imprimir espelho:', error);
      printWindow.close();
      alert('Erro ao preparar a impressão. Por favor, tente novamente.');
    } finally {
      setPrinting(false);
      setExportPdfPayload(null);
    }
  };

  const handleDownloadPDF = async () => {
    if (!activeEmployee || monthlyLogs.length === 0) {
      alert('Não há registros para exportar no período selecionado.');
      return;
    }

    setGeneratingPDF(true);
    try {
      setExportPdfPayload(mountExportPayload());

      await new Promise((resolve) => setTimeout(resolve, 200));

      if (!pdfRef.current) {
        throw new Error('Falha ao montar documento PDF');
      }

      const employeeSlug = activeEmployee.name.replace(/\s+/g, '_').slice(0, 30);
      const filename = `Espelho_Ponto_${employeeSlug}_${selectedMonth}_${selectedYear}.pdf`;

      await exportElementToPdf(pdfRef.current, filename, { orientation: 'l' });
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Por favor, tente novamente.');
    } finally {
      setGeneratingPDF(false);
      setExportPdfPayload(null);
    }
  };

  if (loading && employees.length === 0) {
    return (
      <div className="attendance-log-page-loading">
        <Loader2 className="animate-spin" size={32} />
        <p>Carregando colaboradores...</p>
      </div>
    );
  }

  return (
    <div className="attendance-log-page">
      {/* Main Content: Header & Table */}
      <div className="attendance-log-main">
         {/* User Header & Month Selector */}
         {activeEmployee && (
           <div className="attendance-log-header">
              <button 
                onClick={() => navigate('/erp/documents/attendance-reports')} 
                className="attendance-log-back-btn"
                type="button"
                aria-label="Voltar para relatórios"
              >
                <ArrowLeft size={16} />
              </button>

              <div className="attendance-log-header-content">
              <div className="attendance-log-header-user" data-onboarding="log-employee">
                 <Avatar name={activeEmployee.name} email={activeEmployee.email || ''} imageUrl={activeEmployee.avatar} size="lg" />
                 <div className="attendance-log-header-user-info">
                    <div className="attendance-log-header-name-wrapper" ref={dropdownRef}>
                       <button
                         onClick={() => setShowEmployeeDropdown(!showEmployeeDropdown)}
                         className="attendance-log-header-name-btn"
                       >
                          <h2 className="attendance-log-header-name">{activeEmployee.name}</h2>
                          <ChevronDown 
                            size={16} 
                            className={`attendance-log-header-name-chevron ${showEmployeeDropdown ? 'open' : ''}`}
                          />
                       </button>

                       {/* Dropdown de seleção de colaborador */}
                       {showEmployeeDropdown && (
                         <div className="attendance-log-header-dropdown">
                            <div className="attendance-log-header-dropdown-search">
                               <Search className="attendance-log-header-dropdown-search-icon" size={14} />
                               <input 
                                 type="text" 
                                 placeholder="Buscar colaborador..." 
                                 className="attendance-log-header-dropdown-search-input"
                                 value={searchTerm}
                                 onChange={(e) => setSearchTerm(e.target.value)}
                                 onClick={(e) => e.stopPropagation()}
                               />
                            </div>
                            <div className="attendance-log-header-dropdown-list">
                               {filteredEmployees.length === 0 ? (
                                 <div className="attendance-log-header-dropdown-empty">
                                    <p>Nenhum colaborador encontrado</p>
                                 </div>
                               ) : (
                                 filteredEmployees.map(emp => (
                                   <button
                                     key={emp.id}
                                     onClick={() => handleEmployeeSelect(emp.id.toString())}
                                     className={`attendance-log-header-dropdown-item ${
                                       selectedEmployeeId === emp.id.toString() ? 'active' : ''
                                     }`}
                                   >
                                      <Avatar name={emp.name} email={emp.email || ''} imageUrl={emp.avatar} size="sm" />
                                      <div className="attendance-log-header-dropdown-item-info">
                                         <p className="attendance-log-header-dropdown-item-name">{emp.name}</p>
                                         <p className="attendance-log-header-dropdown-item-role">{emp.role || 'Colaborador'}</p>
                                      </div>
                                   </button>
                                 ))
                               )}
                            </div>
                         </div>
                       )}
                    </div>
                    <div className="attendance-log-header-badges">
                       {activeEmployee.department && (
                         <span className="attendance-log-badge">
                            <Briefcase size={10} /> {activeEmployee.department}
                         </span>
                       )}
                       <span className="attendance-log-badge">
                          ID: #{String(activeEmployee.id).padStart(4, '0')}
                       </span>
                    </div>
                 </div>
              </div>

              <div className="attendance-log-header-actions">
                 <div className="attendance-log-month-selector" data-onboarding="log-month">
                    <button 
                      onClick={handlePreviousMonth}
                      className="attendance-log-month-btn"
                    >
                      <ChevronRight className="rotate-180" size={16} />
                    </button>
                    <span className="attendance-log-month-text">
                      {getMonthName(selectedMonth)} {selectedYear}
                    </span>
                    <button 
                      onClick={handleNextMonth}
                      className="attendance-log-month-btn"
                    >
                      <ChevronRight size={16} />
                    </button>
                 </div>
                 <button
                   type="button"
                   className="attendance-log-tour-help"
                   onClick={() => setTourReplay((current) => current + 1)}
                 >
                    <CircleHelp size={14} /> Como usar
                 </button>
                 <div className="attendance-log-export" data-onboarding="log-export">
                 <button 
                   type="button"
                   className="attendance-log-action-btn attendance-log-action-btn-primary"
                   onClick={handleDownloadPDF}
                   disabled={generatingPDF || loadingLogs || monthlyLogs.length === 0}
                   title="Baixar PDF"
                 >
                    {generatingPDF ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                 </button>
                 <button
                   type="button"
                   className="attendance-log-action-btn attendance-log-action-btn-secondary"
                   onClick={handlePrint}
                   disabled={printing || generatingPDF || loadingLogs || monthlyLogs.length === 0}
                   title="Imprimir espelho"
                 >
                    {printing ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
                 </button>
                 </div>
              </div>
              </div>
           </div>
         )}

         {/* Log Table */}
         <div className="attendance-log-table-wrapper">
            {loadingLogs ? (
              <div className="attendance-log-table-loading">
                 <Loader2 className="animate-spin" size={32} />
                 <p>Carregando registros...</p>
              </div>
            ) : error ? (
              <div className="attendance-log-table-error">
                 <AlertCircle size={24} />
                 <p>{error}</p>
              </div>
            ) : (
              <div className="attendance-log-table-container" data-onboarding="log-table">
                 <p className="attendance-log-table-scroll-hint" aria-hidden="true">
                   Deslize horizontalmente para ver todas as colunas
                 </p>
                 <table className="attendance-log-table">
                    <thead>
                       <tr>
                          <th>Data</th>
                          <th>Dia</th>
                          <th className="text-center" data-onboarding="log-times">Entrada 1</th>
                          <th className="text-center">Saída 1</th>
                          <th className="text-center">Entrada 2</th>
                          <th className="text-center">Saída 2</th>
                          <th className="text-center attendance-log-table-highlight">Total</th>
                          <th className="text-center attendance-log-table-highlight">Banco</th>
                          <th className="text-center">Atestado</th>
                          <th className="text-center">Status</th>
                          <th className="text-center" data-onboarding="log-signature">Assinatura</th>
                       </tr>
                    </thead>
                    <tbody>
                       {monthlyLogs.map((log) => (
                          <tr 
                            key={log.isoDate} 
                            className={log.status === 'weekend' ? 'attendance-log-row-weekend' : ''}
                          >
                             <td className="attendance-log-cell-date">{log.date}</td>
                             <td className="attendance-log-cell-weekday">{log.weekday}</td>
                             
                             {/* Jornada */}
                             {renderTimeCell(log, 'in1')}
                             {renderTimeCell(log, 'out1')}
                             {renderTimeCell(log, 'in2')}
                             {renderTimeCell(log, 'out2')}
                             
                             {/* Totais */}
                             <td className="text-center attendance-log-table-highlight attendance-log-cell-total">
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                   <span>{log.total}</span>
                                   {log.certificates && log.certificates.length > 0 && (
                                      <span className="attendance-log-certificate-hours">
                                         <span aria-hidden="true">📋</span>
                                         <span>+{formatTime(log.certificateHours || 0)}</span>
                                      </span>
                                   )}
                                </div>
                             </td>
                             <td className="text-center attendance-log-table-highlight">
                                <span className={`attendance-log-balance ${
                                  log.balance.startsWith('+') ? 'positive' : 
                                  log.balance.startsWith('-') ? 'negative' : ''
                                }`}>
                                   {log.balance}
                                </span>
                             </td>

                             <td className="text-center">
                                {getCertificateBadge(log.hasFullCertificate || false, log.hasPartialCertificate || false)}
                             </td>

                             <td className="text-center">
                                {getStatusBadge(log.status)}
                             </td>

                             <td className="text-center">
                                {log.signed ? (
                                  <div className="attendance-log-signature-signed" title="Assinado Digitalmente">
                                     <Fingerprint size={18} />
                                  </div>
                                ) : log.status !== 'weekend' ? (
                                  <div className="attendance-log-signature-pending" title="Pendente">
                                     <Clock size={18} />
                                  </div>
                                ) : null}
                             </td>
                          </tr>
                       ))}
                    </tbody>
                 </table>
              </div>
            )}
         </div>

      </div>

      <OnboardingTour
        tourId={ATTENDANCE_LOG_TOUR_ID}
        steps={ATTENDANCE_LOG_ONBOARDING_STEPS}
        userId={user?.id}
        ready={!loading && Boolean(activeEmployee)}
        replayToken={tourReplay}
      />

      {editModal && (
        <div
          className="attendance-log-modal-backdrop"
          role="presentation"
          onClick={() => !savingEdit && setEditModal(null)}
        >
          <div
            className="attendance-log-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="attendance-log-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="attendance-log-edit-title" className="attendance-log-modal-title">
              Ajustar horário
            </h3>
            <p className="attendance-log-modal-meta">
              {editModal.slotLabel} · {editModal.dayDisplay}
            </p>
            <label className="attendance-log-modal-label" htmlFor="attendance-log-time-input">
              Horário
            </label>
            <input
              id="attendance-log-time-input"
              type="time"
              className="attendance-log-modal-input"
              value={editModal.draftTime}
              onChange={(e) =>
                setEditModal((m) => (m ? { ...m, draftTime: e.target.value } : null))
              }
              disabled={savingEdit}
            />
            <div className="attendance-log-modal-actions">
              <button
                type="button"
                className="attendance-log-modal-btn attendance-log-modal-btn-secondary"
                onClick={() => !savingEdit && setEditModal(null)}
                disabled={savingEdit}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="attendance-log-modal-btn attendance-log-modal-btn-primary"
                onClick={saveTimeEdit}
                disabled={savingEdit}
              >
                {savingEdit ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {generatingPDF && (
        <div className="ar-pdf-overlay">
          <div className="ar-pdf-overlay-card">
            <Download size={28} color="#10b981" />
            <h4>Gerando PDF</h4>
            <p>Preparando espelho de ponto...</p>
          </div>
        </div>
      )}

      {exportPdfPayload && (
        <div className="al-pdf-offscreen" aria-hidden>
          <AttendanceLogPdfCanvas ref={pdfRef} {...exportPdfPayload} />
        </div>
      )}
    </div>
  );
};

export default AttendanceLogPage;
